/**
 * Tutorial walkthrough definitions for all major features.
 * Each tutorial has ordered steps with target selectors, position, and content.
 */

import type { DemoSequence } from '@/components/TutorialAnimatedDemo';

export interface TutorialStep {
  /** CSS selector for the element to spotlight (null = center modal) */
  target: string | null;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** Optional animated demo showing cursor interactions */
  demo?: DemoSequence;
}

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  icon: string; // lucide icon name
  category: 'platform' | 'intelligence' | 'analytics' | 'settings';
  /** Route path(s) where this tutorial is most relevant */
  routes?: string[];
  steps: TutorialStep[];
}

export const tutorials: Tutorial[] = [
  {
    id: 'mapping-studio',
    title: 'Map Studio',
    description: 'Learn to visually connect and transform CRM data across systems — no code needed.',
    icon: 'Map',
    category: 'platform',
    routes: ['/studio'],
    steps: [
      { target: null, title: 'Welcome to Map Studio!', content: 'This is where you build the bridges between your CRM systems, ensuring data flows exactly where it needs to go. Let\'s walk through the key features.', position: 'center' },
      { target: '[data-tutorial="source-schema"]', title: 'Source System Fields', content: 'On the left, you\'ll see all available fields from your source CRM. These are the fields Boltway pulls data from. Use search and filters to find what you need.', position: 'right',
        demo: { loop: true, keyframes: [
          { x: 200, y: 350, duration: 400, label: 'Find a field' },
          { x: 200, y: 420, duration: 300, action: 'hover', label: 'Hover to preview' },
          { x: 200, y: 420, duration: 200, action: 'pause' },
        ] },
      },
      { target: '[data-tutorial="target-schema"]', title: 'Target System Fields', content: 'On the right are your destination fields. Drag a connection from any source field to a target field to create a mapping — it\'s that simple.', position: 'left',
        demo: { loop: true, keyframes: [
          { x: 250, y: 400, duration: 400, label: 'Grab source field' },
          { x: 250, y: 400, duration: 200, action: 'drag-start' },
          { x: 600, y: 380, duration: 800, label: 'Drag to target' },
          { x: 900, y: 420, duration: 600, action: 'drag-end', label: 'Drop to connect!' },
        ] },
      },
      { target: '[data-tutorial="ai-map-btn"]', title: 'AI Auto-Mapping', content: 'Save hours of manual work! Click "AI Map" and our AI suggests connections based on field names, types, and context. Accept or reject each suggestion with one click.', position: 'bottom',
        demo: { loop: true, keyframes: [
          { x: 500, y: 60, duration: 500, label: 'Click AI Map' },
          { x: 500, y: 60, duration: 200, action: 'click' },
          { x: 500, y: 60, duration: 600, action: 'pause', label: 'Watch the magic ✨' },
        ] },
      },
      { target: '[data-tutorial="confidence-badge"]', title: 'Confidence Scores', content: 'Each mapping shows a confidence score — green (80%+) means high confidence, yellow (50-80%) needs a glance, red (<50%) needs your review. The AI learns from your choices.', position: 'bottom' },
      { target: '[data-tutorial="transform-btn"]', title: 'Transform Your Data', content: 'Need to reformat dates, split names, or map picklist values? Click the transform icon on any connection to ensure data arrives in the right format.', position: 'bottom' },
      { target: '[data-tutorial="save-mapping"]', title: 'Save & Publish', content: 'Boltway™ autosaves drafts every 10 seconds. When you\'re ready, hit "Publish" to push your mapping live. You can always compare versions and rollback.', position: 'bottom' },
      { target: null, title: 'What\'s Next?', content: 'You\'re ready to start mapping! Select a source and target CRM above, then drag fields to create your first connection. The AI will help you along the way.', position: 'center' },
    ],
  },
  {
    id: 'decision-engine',
    title: 'Decision Engine',
    description: 'Discover how real-time call intelligence identifies key signals and automates sales actions.',
    icon: 'Brain',
    category: 'intelligence',
    routes: ['/engine'],
    steps: [
      { target: null, title: 'Welcome to the Decision Engine!', content: 'This engine translates raw conversation data into actionable insights and automated workflows. It\'s where your sales intelligence comes to life.', position: 'center' },
      { target: '[data-tutorial="signals-tab"]', title: 'Signals: Spot Key Moments', content: 'Signals detect specific patterns in conversations — budget mentions, competitor references, timeline urgency. Each signal carries a confidence weight that trains the system.', position: 'bottom' },
      { target: '[data-tutorial="scores-tab"]', title: 'Scores: Quantify Opportunities', content: 'Scores combine multiple signals into composite metrics like "Deal Health" or "Buyer Readiness." Set thresholds to classify leads as hot, warm, or cold automatically.', position: 'bottom' },
      { target: '[data-tutorial="triggers-tab"]', title: 'Triggers: Automate Actions', content: 'When scores cross your thresholds, triggers fire automatically — update CRM fields, send Slack alerts, or flag deals for manager review. No manual intervention needed.', position: 'bottom' },
      { target: '[data-tutorial="live-panel"]', title: 'Live Intelligence Panel', content: 'During calls, this panel shows real-time signal matches, score updates, and suggested next actions. Your reps see coaching cues without leaving their workflow.', position: 'left' },
      { target: null, title: 'What\'s Next?', content: 'Ready to build your intelligence? Head to the Rules Engine to define custom signals, scores, and triggers tailored to your sales process.', position: 'center' },
    ],
  },
  {
    id: 'crm-connections',
    title: 'CRM Connections',
    description: 'Securely connect your CRM systems and monitor data sync health.',
    icon: 'Plug',
    category: 'settings',
    routes: ['/crm'],
    steps: [
      { target: null, title: 'Connect Your CRMs', content: 'This is where you establish secure connections to 16+ CRM providers including Salesforce, HubSpot, Attio, and Close. OAuth handles authentication so your data stays protected.', position: 'center' },
      { target: '[data-tutorial="add-connection"]', title: 'Add a New Connection', content: 'Click here to connect a CRM provider. You\'ll be redirected to authorize access — Boltway™ never stores your CRM credentials directly.', position: 'bottom' },
      { target: '[data-tutorial="sync-schema"]', title: 'Schema Sync', content: 'After connecting, sync your schema to pull in all objects and fields. Boltway™ automatically monitors for schema changes so your mappings stay accurate.', position: 'bottom' },
      { target: '[data-tutorial="connection-status"]', title: 'Connection Health', content: 'Monitor each connection\'s status, last sync time, and any errors. Boltway™ proactively alerts you when a connection needs attention.', position: 'right' },
      { target: null, title: 'What\'s Next?', content: 'With your CRMs connected, head to Map Studio to start building data bridges between your linked systems!', position: 'center' },
    ],
  },
  {
    id: 'rules-engine',
    title: 'Rules Engine',
    description: 'Define the intelligence logic — what to detect, how to score it, and what actions to take.',
    icon: 'Zap',
    category: 'intelligence',
    routes: ['/rules'],
    steps: [
      { target: null, title: 'Master the Rules Engine', content: 'This is your command center for creating smart rules that drive Boltway™\'s intelligence. Define signals, build scores, and set up automated triggers.', position: 'center' },
      { target: '[data-tutorial="create-signal"]', title: 'Create a Signal', content: 'Define what patterns to detect in conversations. Set keyword matches, context conditions, and confidence weights. For example, create a "Budget Discussion" signal.', position: 'bottom' },
      { target: '[data-tutorial="create-score"]', title: 'Build a Score', content: 'Combine signals into composite scores using weighted formulas. A "High Intent Score" could fire when "Pricing Discussion" and "Next Steps" signals are both detected.', position: 'bottom' },
      { target: '[data-tutorial="create-trigger"]', title: 'Set Up Triggers', content: 'Automate responses when scores hit thresholds — push updates to CRM, notify your team via Slack, or queue deals for review. No manual steps needed.', position: 'bottom' },
      { target: null, title: 'What\'s Next?', content: 'You\'ve built your intelligence rules! Head to the Analytics Dashboard to see how your signals, scores, and triggers are performing in real-time.', position: 'center' },
    ],
  },
  {
    id: 'analytics-dashboard',
    title: 'Analytics Dashboard',
    description: 'Track mapping health, efficiency metrics, and ROI across your integrations.',
    icon: 'BarChart3',
    category: 'analytics',
    routes: ['/dashboard', '/efficiency', '/health'],
    steps: [
      { target: null, title: 'Your Analytics Dashboard', content: 'Get a comprehensive view of your data integration performance. Understand what\'s working, where to improve, and the concrete value Boltway™ delivers.', position: 'center' },
      { target: '[data-tutorial="efficiency-card"]', title: 'Efficiency Metrics', content: 'Track time saved vs manual mapping, AI accuracy rates, and processing throughput. See exactly how automation is accelerating your team\'s workflow.', position: 'bottom' },
      { target: '[data-tutorial="health-card"]', title: 'Mapping Health', content: 'Monitor active mappings, error rates, schema drift alerts, and data confidence scores in real-time. Catch issues before they impact downstream systems.', position: 'bottom' },
      { target: '[data-tutorial="roi-card"]', title: 'ROI Tracking', content: 'See the concrete value Boltway™ delivers — hours saved, error reduction, and projected annual savings. Perfect for stakeholder reporting.', position: 'bottom' },
      { target: null, title: 'What\'s Next?', content: 'Use these insights to optimize your Map Studio configurations and refine your Decision Engine rules for even better results!', position: 'center' },
    ],
  },
  {
    id: 'team-management',
    title: 'Team & Permissions',
    description: 'Invite team members, assign roles, and control who can access what.',
    icon: 'Users',
    category: 'settings',
    routes: ['/team'],
    steps: [
      { target: null, title: 'Team Management', content: 'Collaborate effectively by managing your team\'s access and roles. Ensure everyone has the right permissions to contribute without risking configuration changes.', position: 'center' },
      { target: '[data-tutorial="invite-member"]', title: 'Invite Team Members', content: 'Add colleagues by email. They\'ll receive an invitation to join your organization with the role you assign — no account creation needed on their end.', position: 'bottom' },
      { target: '[data-tutorial="role-selector"]', title: 'Roles & Permissions', content: 'Admin: full access to all features. Editor: create and modify mappings and rules. Viewer: read-only access for monitoring. Choose carefully to protect sensitive configurations.', position: 'bottom' },
      { target: null, title: 'What\'s Next?', content: 'Your team is set up! Make sure everyone explores the tutorials for their area — Map Studio for data teams, Decision Engine for sales ops.', position: 'center' },
    ],
  },
];

export function getTutorialsByCategory(category: Tutorial['category']): Tutorial[] {
  return tutorials.filter(t => t.category === category);
}

export function getTutorialById(id: string): Tutorial | undefined {
  return tutorials.find(t => t.id === id);
}

export function getTutorialsForRoute(pathname: string): Tutorial[] {
  return tutorials.filter(t => t.routes?.some(r => pathname.startsWith(r)));
}
